import React, { useState, useEffect } from "react";
import { getCardValue } from "../resultVisuals/cardAssets";
import styles from "./VideoBox.module.css";
import { Modal } from "react-bootstrap";
import { baseCasinoType } from "../../../utils/casinoGame";
import { getCasinoRulesHtml } from "../../../data/casinoRulesHtml";
import { IS_REF_MOBILE } from "../../../utils/refViewport";
import {
  VideoBoxTitle,
  VideoBoxTimer,
  MogamboCardsOverlay,
  VideoBoxResultOverlay,
  TeenUniqueCardsOverlay,
  Race20CardsOverlay,
  QueenCardsOverlay,
  Teen1DayCardsOverlay,
  Teen20CardsOverlay,
} from "./overlays";

const VideoBox = ({
  playerA,
  playerB,
  iframeSrc,
  timerValue,
  gameType: rawGameType,
  resultData,
  poisonCard,
  roundId,
  subData,
  winIndex = -1,
  videoTitle,
  showTitle = false,
  onRulesClick,
  cardsOverlay,
  titleNode, // optional: reference .casino-video-title rendered as the FIRST child (replaces the built-in title)
  className = "", // optional: extra classes on the root .casino-video (e.g. reference "mt-0")
}) => {
  // Virtual feed (vteen20, vlucky7, ...) ka layout normal game jaisa hi hai
  const gameType = rawGameType ? baseCasinoType(rawGameType) : rawGameType;
  const [iframeError, setIframeError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  // Rules (video (i) icon): in-video panel; reference also opens a modal when IS_MOBILE (<1280)
  const [showRules, setShowRules] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const rulesHtml = getCasinoRulesHtml(rawGameType);
  const handleRulesClick = () => {
    if (onRulesClick) onRulesClick();
    if (!rulesHtml) return;
    setShowRules(true);
    // Reference: closing the modal leaves the in-video panel open (separate state)
    if (IS_REF_MOBILE) setShowRulesModal(true);
  };

  // Normalize card data: API may return string "6DD,7CC,..." or array ["6DD","7CC",...]
  const normalizedResultData =
    Array.isArray(resultData)
      ? resultData.join(",")
      : typeof resultData === "string"
        ? resultData
        : resultData ?? "";

  // Mogambo: parse card string → Daga/Teja (first 2), Mogambo (1). Total = sum of face values.
  const mogamboCards = (() => {
    if (gameType !== "mogambo") return null;
    const cards = String(normalizedResultData)
      .split(",")
      .map((c) => c.trim())
      .filter(Boolean);
    const dagaTeja = [cards[0] || "1", cards[1] || "1"];
    const mogambo = [cards[2] || "1"];
    const allThree = [dagaTeja[0], dagaTeja[1], mogambo[0]];
    const total = allThree.reduce((sum, token) => sum + getCardValue(token), 0);
    const allPlaceholder =
      dagaTeja.every((c) => !c || c === "0" || c === "1") &&
      mogambo.every((c) => !c || c === "0" || c === "1");
    return { dagaTeja, mogambo, allPlaceholder, total };
  })();

  const handleIframeError = () => {
    setIframeError(true);
    setErrorMessage("Video stream unavailable");
  };

  useEffect(() => {
    setIframeError(false);
    setErrorMessage("");
  }, [iframeSrc]);


  const isMogambo = gameType === "mogambo";
  const isTeenunique = gameType === "teenunique";
  const isRace20 = gameType === "race20";
  const isQueen = gameType === "queen";
  const isTeen1Day = gameType === "teen33" || gameType === "teen32" || gameType === "teen3" || gameType === "teen" || gameType === "pteen" || gameType === "teenmuf";
  const isTeen20 = gameType === "teen20" || gameType === "teen20b" || gameType === "pteen20";

  // Unique Teenpatti: 6 cards from result string
  const teenuniqueCards = isTeenunique
    ? String(normalizedResultData).split(",").map((c) => c.trim()).filter(Boolean).slice(0, 6)
    : [];

  return (
    // Reference markup: .casino-video > .video-box-container > .video-box > iframe,
    // then the card overlay, the flip-clock timer and the rules icon (index.css positions them).
    <div className={`${isTeenunique && cardsOverlay === undefined ? `casino-video ${styles.videoBox} ${styles.teenunique}` : "casino-video"}${className ? ` ${className}` : ""}`}>
      {titleNode}
      <div className="video-box-container">
        <div className="video-box">
          {iframeError ? (
            <div className={styles.errorMessage}>
              <div>🎥</div>
              <div>{errorMessage}</div>
              <div style={{ fontSize: "0.9rem", marginTop: "0.5rem", opacity: 0.8 }}>
                Please check your connection or try again later
              </div>
            </div>
          ) : (
            <iframe
              src={iframeSrc || undefined}
              title="Live Stream"
              allowFullScreen
              onError={handleIframeError}
              onLoad={(e) => {
                try {
                  const iframeDoc =
                    e.target.contentDocument || e.target.contentWindow?.document;
                  if (
                    iframeDoc?.body?.innerText?.includes("not allowed")
                  ) {
                    handleIframeError();
                    setErrorMessage("Stream access not allowed");
                  }
                } catch {
                  console.log(
                    "Cannot access iframe content due to CORS"
                  );
                }
              }}
            />
          )}
        </div>
      </div>
      <VideoBoxTitle
        show={titleNode === undefined && (isMogambo || isRace20 || isQueen || showTitle)}
        title={isMogambo ? "Mogambo" : isRace20 ? "Race 20" : isQueen ? "Queen" : videoTitle}
        roundId={roundId}
      />
      {/* A page can pass its own reference-markup card overlay (cardsOverlay); otherwise the built-in one */}
      {cardsOverlay !== undefined ? cardsOverlay : (
        <>
          {isMogambo ? (
            <MogamboCardsOverlay mogamboCards={mogamboCards} />
          ) : isTeenunique ? (
            <TeenUniqueCardsOverlay cardTokens={teenuniqueCards} />
          ) : isRace20 ? (
            <Race20CardsOverlay normalizedResultData={normalizedResultData} />
          ) : isQueen ? (
            <QueenCardsOverlay
              normalizedResultData={normalizedResultData}
              subData={subData}
              winIndex={winIndex}
            />
          ) : isTeen1Day ? (
            <Teen1DayCardsOverlay normalizedResultData={normalizedResultData} />
          ) : isTeen20 ? (
            <Teen20CardsOverlay normalizedResultData={normalizedResultData} />
          ) : (
            <VideoBoxResultOverlay
              gameType={gameType}
              normalizedResultData={normalizedResultData}
              poisonCard={poisonCard}
              playerA={playerA}
              playerB={playerB}
            />
          )}
        </>
      )}

      <VideoBoxTimer timerValue={timerValue} />
      <div className="casino-video-right-icons">
        <div title="Rules" className="casino-video-rules-icon" onClick={handleRulesClick}>
          <i className="fas fa-info-circle" />
        </div>
      </div>
      <div>
        {/* Reference renders the in-video panel in every mode (.d-none-small hides it <768px) and,
            when IS_MOBILE, also opens the rules modal (whose body its CSS keeps hidden). */}
        {showRules && (
          <div
            id="casino-vieo-rules"
            aria-modal="true"
            className="casino-vieo-rules show d-none-small show-rules"
            style={{ display: "block" }}
          >
            <div className="rules-header">
              <div>Rules</div>{" "}
              <i className="fas fa-times" onClick={() => setShowRules(false)} />
            </div>{" "}
            <div className="rules-body" dangerouslySetInnerHTML={{ __html: rulesHtml }} />
          </div>
        )}
        <Modal
          show={showRulesModal}
          onHide={() => setShowRulesModal(false)}
          className="casino-rules teenpatti2-rule-modal"
          dialogClassName="modal-md"
          id="rules-modal"
        >
          {/* Reference (bootstrap-vue) header: header > h5.modal-title + button.close "×" */}
          <header className="modal-header">
            <h5 className="modal-title">Rules</h5>{" "}
            <button type="button" aria-label="Close" className="close" onClick={() => setShowRulesModal(false)}>
              ×
            </button>
          </header>
          <Modal.Body>
            <div className="casino-vieo-rules show-rules show">
              <div className="rules-body" dangerouslySetInnerHTML={{ __html: rulesHtml || "" }} />
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
};

export default VideoBox;
